/**
 * Ajustos. docs/02 §9.
 *
 * Graella de 220px + contingut, màxim 1100px. Vuit pestanyes: General · Àmbits ·
 * Calendaris · MCP i API · Usuari IA · Compartits · Perfil · Admin.
 *
 * **Dins d'Ajustos no hi ha ni switch de vista ni chips d'àmbit.** El brief hi insisteix
 * (línia 41) i el prototip encara els deixa: aquí la barra superior porta només el
 * wordmark i "‹ Tornar al tauler". Per això aquesta pantalla no munta `TopBar`.
 */

import { useEffect, useState } from 'react';
import {
  DEFAULT_MAIL_TEMPLATE,
  dateTime,
  getLocale,
  renderMailTitle,
  resolveWeekStart,
  t,
  unknownMailVars,
  weekdayNames,
} from '@fem-ho/contracts';
import { v7 as uuidv7 } from 'uuid';
import { EmptyState, useIsMobile } from '@fem-ho/design-system/femho';
import { api, ApiError, failureText } from '../app/api.js';
import { Avatar } from '../app/Avatar.js';
import { canChooseScopeMode, resolveScopeMode } from '../app/scope-mode.js';
import { Chips } from '../app/Chips.js';
import { Brand } from '../app/Brand.js';
import { useRouter } from '../app/router.js';
import { useSession, useSessionData } from '../app/session.js';
import { useApi, useMutation } from '../app/useApi.js';
import type {
  AdminUser,
  Agent,
  ApiTokenSummary,
  ScopeSettings,
  TaskType,
  Calendar,
  Info,
  MailAccount,
  MailRule,
  MailTestResult,
  Member,
  Project,
  Scope,
  ShareSummary,
  UpdateStatus,
} from '../app/types.js';
import { ErrorBanner } from './BoardScreen.js';
import { GoogleMailPanel } from './GoogleMailPanel.js';
import { ExternalAccessPanel, AgentTokenEditor } from './ExternalAccessPanel.js';

/**
 * **El correu va en pestanya pròpia i no dins de «Calendaris».**
 *
 * Aquelles fonts són **de l'àmbit** i aquestes són **teves**: un calendari subscrit el veu
 * tot qui és a l'àmbit, i un compte de correu no el veu ningú més. Posar-los junts
 * convidaria a pensar el contrari, que amb una contrasenya personal pel mig és car.
 */
const TABS = [
  'general',
  'scopes',
  'calendars',
  'mail',
  'mcp',
  'ai',
  'shares',
  'profile',
  'admin',
] as const;
type Tab = (typeof TABS)[number];

export function SettingsScreen() {
  const { profile } = useSessionData();
  const { navigate, route } = useRouter();
  const mobile = useIsMobile();

  /**
   * La pestanya pot venir de la URL.
   *
   * **Perquè s'hi pugui enviar algú.** El menú `+` de la barra diu "Nou projecte" i
   * portava a Ajustos i prou: la persona queia a "General" i havia de trobar sola que els
   * projectes són a "Àmbits". Un menú que promet una acció i et deixa a la porta d'un
   * edifici no ha fet la seva feina.
   */
  // Admin només per a administradors: amagar-la no és seguretat —el servidor ja hi posa
  // `users:manage`— però ensenyar una pestanya que sempre dona 403 és una mala broma.
  const tabs = TABS.filter((key) => key !== 'admin' || profile.role === 'admin');

  /**
   * La pestanya pot venir de la URL, **d'entre les que aquesta persona té**.
   *
   * Es validava contra la llista sencera i no contra la seva: `?tab=admin` sense ser
   * administrador pintava el panell d'Admin amb un «Alguna cosa ha fallat» en vermell,
   * que és la mateixa mala broma que el comentari de sobre evita al menú, per una altra
   * porta. Ara cau a General, com qualsevol pestanya que no existeix.
   */
  const fromQuery = route.query.get('tab');
  const [tab, setTab] = useState<Tab>(
    tabs.includes(fromQuery as Tab) ? (fromQuery as Tab) : 'general',
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          background: 'var(--sidebar-bg)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--card-border)',
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            padding: '12px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 22,
          }}
        >
          <Brand />
          <button
            type="button"
            data-testid="settings-back"
            onClick={() => navigate('/')}
            style={{
              border: 'none',
              background: 'transparent',
              font: 'inherit',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--ink-soft)',
            }}
          >
            {t('nav.backToBoard')}
          </button>
        </div>
      </header>

      {/*
        **Al mòbil, el menú va a dalt i no al costat.**

        Amb `220px 1fr` fixos, a una pantalla de 390 el contingut es quedava amb **cent
        deu píxels**: el text queia a una paraula per línia, el selector de seguretat
        ensenyava «TLS · p» i el botó «Afegeix el compte» era un cercle amb tres línies a
        dins. No era una pantalla incòmoda, era una pantalla que no es pot fer servir —i
        Ajustos al mòbil és justament on es configura el correu i els calendaris.

        A dalt i en fila, amb desplaçament horitzontal **dins de la barra** i no de la
        pàgina: el que no pot passar és que la pàgina sencera es mogui de costat.
      */}
      <div
        data-testid="settings-screen"
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: mobile ? '16px 14px' : '24px 28px',
          display: 'grid',
          gridTemplateColumns: mobile ? '1fr' : '220px 1fr',
          gap: mobile ? 14 : 28,
          alignItems: 'start',
        }}
      >
        <nav
          style={
            mobile
              ? {
                  display: 'flex',
                  gap: 6,
                  overflowX: 'auto',
                  paddingBottom: 4,
                  // Sense això, els xips s'encongeixen fins a partir el nom.
                  scrollbarWidth: 'none',
                }
              : { display: 'grid', gap: 2 }
          }
        >
          {tabs.map((key) => (
            <button
              key={key}
              type="button"
              data-testid={`settings-tab-${key}`}
              aria-current={tab === key}
              onClick={() => setTab(key)}
              style={{
                textAlign: 'left',
                padding: '9px 12px',
                borderRadius: mobile ? 100 : 10,
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 13,
                fontWeight: tab === key ? 700 : 500,
                background: tab === key ? 'var(--ghost-bg)' : 'transparent',
                color: 'var(--ink)',
                ...(mobile ? { flexShrink: 0, whiteSpace: 'nowrap' as const } : {}),
              }}
            >
              {t(`settings.tab.${key}`)}
            </button>
          ))}
        </nav>

        <section>
          {tab === 'general' ? <GeneralTab /> : null}
          {tab === 'scopes' ? <ScopesTab /> : null}
          {tab === 'calendars' ? <CalendarsTab /> : null}
          {tab === 'mail' ? <MailTab /> : null}
          {tab === 'mcp' ? <ExternalAccessPanel onGoToAgent={() => setTab('ai')} /> : null}
          {tab === 'ai' ? <AiTab /> : null}
          {tab === 'shares' ? <SharesTab /> : null}
          {tab === 'profile' ? <ProfileTab /> : null}
          {tab === 'admin' ? <AdminTab /> : null}
        </section>
      </div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-card)',
        padding: 18,
        marginBottom: 16,
        display: 'grid',
        gap: 12,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{title}</h2>
      {children}
    </section>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  testId,
  disabled = false,
}: {
  disabled?: boolean;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  testId: string;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span style={{ color: 'var(--ink-soft)' }}>{label}</span>
    </label>
  );
}

/**
 * Si hi ha una versió més nova.
 *
 * **Un error de xarxa no es dibuixa com un "estàs al dia".** Una instància sense sortida
 * a internet ho estaria dient sempre, i callaria justament el dia que hi ha una
 * actualització de seguretat: el servidor distingeix `unreachable` d'`ok` i aquí es diu.
 *
 * Quan no hi ha res a dir —vas al dia, o la comprovació està apagada— **no es pinta res**.
 * Un "estàs al dia" permanent és una línia que la gent aprèn a no llegir, i llavors
 * l'avís que sí que importa cau al mateix sac.
 */
function UpdateNotice() {
  const update = useApi<UpdateStatus>('/api/v1/updates').data;
  if (update === undefined) return null;

  if (update.reason === 'unreachable') {
    return (
      <span data-testid="update-unreachable" style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        {t('settings.update.unreachable')}
      </span>
    );
  }
  if (!update.available || update.latest === null) return null;

  return (
    <span
      data-testid="update-available"
      style={{
        fontSize: 12,
        color: 'var(--ink)',
        background: 'var(--tag-bg)',
        borderRadius: 10,
        padding: '9px 11px',
      }}
    >
      {t('settings.update.available', { version: update.latest })}{' '}
      {update.url === null ? null : (
        <a href={update.url} target="_blank" rel="noreferrer noopener">
          {t('settings.update.see')}
        </a>
      )}
    </span>
  );
}

function GeneralTab() {
  const { profile, settings, instance } = useSessionData();
  const weekStart = resolveWeekStart(settings.week_start, getLocale());
  // `/info` és públic i sense autenticar: el dret al codi el té qualsevol que hi arribi.
  const info = useApi<Info>('/info').data ?? { version: '', license: '', source_url: '' };
  const { updateProfile, updateSettings } = useSession();
  const [showTaskTime, setShowTaskTime] = useState(settings.show_task_time !== false);
  useEffect(() => setShowTaskTime(settings.show_task_time !== false), [settings.show_task_time]);
  const timePreference = useMutation(async (value: boolean) => {
    try {
      await updateSettings({ show_task_time: value });
    } catch (cause) {
      setShowTaskTime(settings.show_task_time !== false);
      throw cause;
    }
  });
  const [showReports, setShowReports] = useState(settings.show_reports !== false);
  useEffect(() => setShowReports(settings.show_reports !== false), [settings.show_reports]);
  const reportsPreference = useMutation(async (value: boolean) => {
    try {
      await updateSettings({ show_reports: value });
    } catch (cause) {
      setShowReports(settings.show_reports !== false);
      throw cause;
    }
  });

  return (
    <>
      {/*
        L'idioma va el primer de tot.
        És l'única preferència que canvia la pantalla on l'estàs triant: si algú hi ha
        arribat perquè l'app li surt en un idioma que no és el seu, ha de ser la primera
        cosa que trobi i no la sisena.
      */}
      <Group title={t('settings.language')}>
        <Chips
          testId="language-chips"
          value={profile.locale}
          options={[
            /**
             * check-ignore · cada idioma es diu **en el seu**.
             *
             * Qui busca el castellà busca "Español", no "Castellà", i qui busca el
             * català el busca escrit "Català" encara que tingui l'app en anglès. Passar
             * aquests tres noms pel catàleg voldria dir que la llista es tradueix
             * sencera i que ningú hi troba el seu.
             */
            { key: 'ca' as const, label: 'Català' },
            { key: 'en' as const, label: 'English' },
            { key: 'es' as const, label: 'Español' }, // check-ignore · veure a dalt
          ]}
          onChange={(locale) => void updateProfile({ locale })}
        />
      </Group>

      {/*
        El primer dia de la setmana, just sota l'idioma.
        `auto` el treu de la llengua —dilluns en català i castellà, diumenge en anglès—
        i aquí es pot manar per damunt: **no és només una convenció lingüística**, qui
        treballa el cap de setmana el vol d'una manera i qui no, d'una altra, i tots dos
        poden tenir la mateixa llengua.
      */}
      {/*
        **Multiàmbit o monoàmbit.**

        Va aquí i a dalt de tot de les preferències de disposició perquè és la que canvia
        més coses de cop: què hi ha a la barra, i si el camp d'afegida demana `#Àmbit`.

        Quan la instància ho ha decidit per tothom, surt **desactivat amb el motiu** i no
        amagat: un ajust que desapareix fa pensar que l'app l'ha perdut, i qui l'ha buscat
        a la documentació el ve a trobar aquí.
      */}
      <Group title={t('settings.scopeMode')}>
        <Chips
          testId="scope-mode"
          groupLabel={t('settings.scopeMode')}
          value={resolveScopeMode(instance, settings)}
          disabled={!canChooseScopeMode(instance)}
          options={[
            {
              key: 'multi' as const,
              label: t('settings.scopeMode.multi'),
              hint: t('settings.scopeMode.multi.hint'),
            },
            {
              key: 'single' as const,
              label: t('settings.scopeMode.single'),
              hint: t('settings.scopeMode.single.hint'),
            },
          ]}
          onChange={(value) => void updateSettings({ scope_mode: value })}
        />
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          {canChooseScopeMode(instance)
            ? t('settings.scopeMode.help')
            : t('settings.scopeMode.fixed', {
                mode: t(`settings.scopeMode.${instance.scope_mode ?? 'both'}`),
              })}
        </p>
      </Group>

      <Group title={t('tracking.cardsTitle')}>
        <Toggle
          testId="settings-show-task-time"
          checked={showTaskTime}
          disabled={timePreference.busy}
          label={t('tracking.showCards')}
          onChange={(value) => {
            setShowTaskTime(value);
            void timePreference.run(value);
          }}
        />
        <p>{t('tracking.cardsHint')}</p>
        {timePreference.error && <p role="alert">{failureText(timePreference.error)}</p>}
      </Group>
      <Group title={t('reports.title')}>
        <Toggle
          testId="settings-show-reports"
          checked={showReports}
          disabled={reportsPreference.busy}
          label={t('settings.showReports')}
          onChange={(value) => {
            setShowReports(value);
            void reportsPreference.run(value);
          }}
        />
        <p>{t('settings.showReportsHint')}</p>
        {reportsPreference.error && <p role="alert">{failureText(reportsPreference.error)}</p>}
      </Group>
      <Group title={t('settings.weekStart')}>
        <Chips
          testId="week-start"
          value={settings.week_start ?? 'auto'}
          options={[
            {
              key: 'auto' as const,
              label: t('settings.weekStart.auto', {
                day: weekdayNames(getLocale(), weekStart)[0] ?? '',
              }),
            },
            { key: 'monday' as const, label: weekdayNames(getLocale(), 1)[0] ?? '' },
            { key: 'sunday' as const, label: weekdayNames(getLocale(), 0)[0] ?? '' },
          ]}
          onChange={(value) => void updateSettings({ week_start: value })}
        />
      </Group>

      {/*
        Què li passa a la cita quan s'esborra la tasca que en va sortir.

        Viu aquí i no a la fitxa de cada esdeveniment perquè és **una manera de treballar**
        i no una decisió per cas: qui esborra una tasca derivada vol sempre el mateix, i
        preguntar-ho cada vegada seria un diàleg més al camí d'una acció que ja en té un.
      */}
      <Group title={t('settings.events.onDelete')}>
        <Chips
          testId="event-task-deleted"
          value={settings.event_task_deleted ?? 'return_to_inbox'}
          options={[
            {
              key: 'return_to_inbox' as const,
              label: t('settings.events.onDelete.return'),
              hint: t('settings.events.onDelete.returnHint'),
            },
            {
              key: 'hide_from_inbox' as const,
              label: t('settings.events.onDelete.hide'),
              hint: t('settings.events.onDelete.hideHint'),
            },
          ]}
          onChange={(value) => void updateSettings({ event_task_deleted: value })}
        />
      </Group>

      {/*
        **Article 13 de l'AGPL, no un crèdit.**

        Qui fa servir aquesta instància per la xarxa té dret al codi de la versió que li
        estan servint, i la manera d'oferir-lo és dir-li on és. La URL surt de `/info`,
        que la porta configurable: qui publiqui una versió modificada hi posarà la seva.
      */}
      <Group title={t('settings.about')}>
        <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          {t('settings.source', { version: info.version, license: info.license ?? '' })}{' '}
          <a href={info.source_url ?? ''} target="_blank" rel="noreferrer noopener">
            {t('settings.sourceLink')}
          </a>
        </span>

        <UpdateNotice />

        {/*
          Els crèdits.

          **Plou hi surt amb nom propi i no com una dependència més**, perquè no ho és:
          `NOTICE` diu que és el design system d'un producte a part, vendoritzat aquí tal
          com ve i **amb condicions pròpies que no cobreix l'AGPL d'aquest repositori**.
          Amagar-ho a una llista de llibreries seria fer passar per nostre el que no ho és.
        */}
        <span style={{ fontSize: 11.5, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
          {t('settings.credits')}
        </span>
      </Group>

      <Group title={t('settings.theme')}>
        <Chips
          testId="theme-chips"
          value={profile.theme}
          options={[
            { key: 'system' as const, label: t('settings.theme.system') },
            { key: 'light' as const, label: t('settings.theme.light') },
            { key: 'dark' as const, label: t('settings.theme.dark') },
          ]}
          onChange={(theme) => void updateProfile({ theme })}
        />
      </Group>

      <Group title={t('settings.accent')}>
        <Chips
          testId="accent-chips"
          value={profile.accent}
          options={[
            { key: 'default' as const, label: t('settings.accent.default') },
            { key: 'soft' as const, label: t('settings.accent.soft') },
            { key: 'mono-warm' as const, label: t('settings.accent.mono-warm') },
            { key: 'mono-cool' as const, label: t('settings.accent.mono-cool') },
          ]}
          onChange={(accent) => void updateProfile({ accent })}
        />
      </Group>

      <Group title={t('settings.dashboardItems')}>
        <Toggle
          testId="toggle-calendar-widget"
          label={t('settings.showCalendarWidget')}
          checked={settings.show_calendar_widget !== false}
          onChange={(value) => void updateSettings({ show_calendar_widget: value })}
        />
        <Toggle
          testId="toggle-overdue-section"
          label={t('settings.showOverdueSection')}
          checked={settings.show_overdue_section !== false}
          onChange={(value) => void updateSettings({ show_overdue_section: value })}
        />
      </Group>

      <Group title={t('settings.inboxPosition')}>
        <Chips
          testId="inbox-position"
          value={settings.inbox_position ?? 'right'}
          options={[
            { key: 'left' as const, label: t('settings.inbox.left') },
            { key: 'right' as const, label: t('settings.inbox.right') },
            { key: 'below' as const, label: t('settings.inbox.below') },
          ]}
          onChange={(position) => void updateSettings({ inbox_position: position })}
        />
        <Toggle
          testId="toggle-inbox-overdue"
          label={t('settings.inboxShowOverdue')}
          checked={settings.inbox_show_overdue !== false}
          onChange={(value) => void updateSettings({ inbox_show_overdue: value })}
        />
      </Group>
    </>
  );
}

/**
 * Els vuit colors d'àmbit de la paleta ampliada.
 *
 * `docs/00` reserva la tríada de Plou per als tres àmbits inicials i dona als que crea
 * l'usuari una paleta pròpia. Els vuit tokens existien a `femho/tokens.css` des del
 * primer dia i **no els feia servir ningú**: tots els àmbits creats des d'aquí sortien
 * blaus perquè el color anava cablejat.
 */
const SCOPE_COLORS = [
  '--femho-scope-1',
  '--femho-scope-2',
  '--femho-scope-3',
  '--femho-scope-4',
  '--femho-scope-5',
  '--femho-scope-6',
  '--femho-scope-7',
  '--femho-scope-8',
] as const;

function ColorPicker({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (color: string) => void;
  testId: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={t('settings.scopeColor')}
      data-testid={testId}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}
    >
      {SCOPE_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={color}
          data-testid={`${testId}-${color}`}
          onClick={() => onChange(color)}
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: `var(${color})`,
            cursor: 'pointer',
            // La selecció es marca amb un anell separat i no amb una vora, que taparia
            // el color just al color que s'està triant.
            border: '2px solid var(--panel-bg)',
            boxShadow: value === color ? '0 0 0 2px var(--ink)' : 'none',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Els projectes d'un àmbit, i com se'n fa un de nou.
 *
 * **Agrupats per àmbit i no en una llista plana** perquè un projecte no existeix sol: la
 * pregunta que es fa qui arriba aquí és "quins projectes té Feina", no "quins projectes
 * hi ha". I és la mateixa forma que després té el desplegable del xip.
 */
function ProjectsOfScope({ scope }: { scope: Scope }) {
  const projects = useApi<Project[]>(`/api/v1/projects?scope_id=${scope.id}`);
  const { refreshEntities } = useSession();
  const [name, setName] = useState('');

  const create = useMutation(async () => {
    if (name.trim() === '') return;
    await api.post('/api/v1/projects', { id: uuidv7(), scope_id: scope.id, name: name.trim() });
    setName('');
    projects.reload();
    // La llista de la sessió és la que llegeixen els xips i els suggeriments de `#`:
    // sense refrescar-la, el projecte nou no existiria enlloc fora d'Ajustos.
    void refreshEntities();
  });

  const rows = (projects.data ?? []).filter((project) => project.scope_id === scope.id);

  return (
    <div style={{ display: 'grid', gap: 6, paddingBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden="true"
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: `var(${scope.color})`,
          }}
        />
        <span style={{ fontSize: 12.5, fontWeight: 700 }}>{scope.name}</span>
      </div>

      {rows.length === 0 ? (
        <p style={{ ...HINT, paddingLeft: 17 }}>{t('settings.noProjects')}</p>
      ) : (
        rows.map((project) => (
          <div
            key={project.id}
            data-testid={`project-row-${project.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingLeft: 17,
              fontSize: 12.5,
            }}
          >
            <span style={{ flex: 1 }}>{project.name}</span>
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              aria-label={t('settings.projectDelete')}
              style={{ color: 'var(--danger-text)' }}
              onClick={() => {
                void api.delete(`/api/v1/projects/${project.id}`).then(() => {
                  projects.reload();
                  void refreshEntities();
                });
              }}
            >
              ×
            </button>
          </div>
        ))
      )}

      <div style={{ display: 'flex', gap: 8, paddingLeft: 17 }}>
        <input
          className="plou-input"
          data-testid={`new-project-${scope.id}`}
          value={name}
          placeholder={t('settings.projectName')}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void create.run();
          }}
        />
        <button
          type="button"
          className="plou-btn"
          data-testid={`new-project-create-${scope.id}`}
          disabled={create.busy}
          onClick={() => void create.run()}
        >
          {t('nav.create')}
        </button>
      </div>
    </div>
  );
}

function ScopeRow({ scope, onDone }: { scope: Scope; onDone: () => Promise<void> }) {
  const { profile } = useSessionData();
  // Qui mana a l'àmbit. `owner_id` mana sempre, hi hagi fila de membre o no.
  const isOwner = scope.owner_id === profile.id;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scope.name);
  const [color, setColor] = useState(scope.color);
  const [kind, setKind] = useState<'individual' | 'collective'>(scope.kind);
  const [openMembers, setOpenMembers] = useState(false);
  const [openTracking, setOpenTracking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation(async () => {
    setError(null);
    try {
      await api.patch(`/api/v1/scopes/${scope.id}`, { name: name.trim(), color, kind });
      setEditing(false);
      await onDone();
    } catch (problem) {
      // El servidor diu QUI bloqueja el canvi de tipus i QUANTES coses queden a dins,
      // i `ApiError` ja en porta el text traduït. Ensenyar "no s'ha pogut" seria
      // fer-li endevinar què li falta.
      setError(problem instanceof ApiError ? problem.message : t('error.generic'));
    }
  });

  const remove = useMutation(async () => {
    setError(null);
    try {
      await api.delete(`/api/v1/scopes/${scope.id}`);
      await onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : t('error.generic'));
    }
  });

  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid={`scope-row-${scope.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span
          aria-hidden="true"
          style={{ width: 9, height: 9, borderRadius: '50%', background: `var(${scope.color})` }}
        />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{scope.name}</span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
          {t(`settings.scopeKind.${scope.kind}`)}
        </span>
        <button
          type="button"
          data-testid={`scope-edit-${scope.id}`}
          onClick={() => setEditing(!editing)}
          style={LINK_BUTTON}
        >
          {t('settings.scopeEdit')}
        </button>
        {scope.kind === 'collective' ? (
          <button
            type="button"
            data-testid={`scope-members-${scope.id}`}
            onClick={() => setOpenMembers(!openMembers)}
            style={LINK_BUTTON}
          >
            {t('settings.members')}
          </button>
        ) : null}
        {isOwner ? (
          <button
            type="button"
            data-testid={`scope-tracking-${scope.id}`}
            onClick={() => setOpenTracking(!openTracking)}
            style={LINK_BUTTON}
          >
            {t('settings.tracking')}
          </button>
        ) : null}
      </div>

      {editing ? (
        <div style={{ display: 'grid', gap: 8, paddingLeft: 18 }}>
          <input
            className="plou-input"
            data-testid={`scope-name-${scope.id}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <ColorPicker value={color} onChange={setColor} testId={`scope-color-${scope.id}`} />
          <Chips
            testId={`scope-kind-${scope.id}`}
            value={kind}
            options={[
              { key: 'individual' as const, label: t('settings.scopeKind.individual') },
              { key: 'collective' as const, label: t('settings.scopeKind.collective') },
            ]}
            onChange={setKind}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="plou-btn plou-btn-primary"
              data-testid={`scope-save-${scope.id}`}
              disabled={save.busy}
              onClick={() => void save.run()}
            >
              {t('settings.scopeSave')}
            </button>
            <button
              type="button"
              className="plou-btn"
              data-testid={`scope-delete-${scope.id}`}
              disabled={remove.busy}
              onClick={() => {
                if (window.confirm(t('settings.scopeDeleteConfirm', { name: scope.name }))) {
                  void remove.run();
                }
              }}
            >
              {t('settings.scopeDelete')}
            </button>
          </div>
          {error === null ? null : (
            <p
              data-testid={`scope-error-${scope.id}`}
              style={{ fontSize: 12, color: 'var(--danger-text)', margin: 0 }}
            >
              {error}
            </p>
          )}
        </div>
      ) : null}

      {openMembers ? <MembersList scopeId={scope.id} canManage={isOwner} /> : null}
      {openTracking ? <TrackingSettings scopeId={scope.id} /> : null}
    </div>
  );
}

/**
 * Com es comporta un àmbit: el registre de dedicació, l'horari i les tipologies.
 *
 * Va aquí i no a una pestanya pròpia perquè és **d'aquest àmbit**: qui fa servir Fem-ho per
 * a casa no ho ha de veure mai, i qui factura hores ho troba on ja és per canviar el nom i
 * el color.
 */
function TrackingSettings({ scopeId }: { scopeId: string }) {
  const settings = useApi<ScopeSettings>(`/api/v1/scopes/${scopeId}/settings`);
  const types = useApi<{ data: TaskType[] }>(`/api/v1/task-types?scope_id=${scopeId}`);
  const [nou, setNou] = useState('');
  const [recuperats, setRecuperats] = useState<number | null>(null);

  const config = settings.data;

  const desa = (canvis: Record<string, unknown>): void => {
    void api
      .patch<{ backfilled: number }>(`/api/v1/scopes/${scopeId}/settings`, canvis)
      .then((resposta) => {
        // **Es diu quants blocs s'han recuperat.** Encendre-ho i trobar-se el Registre ple
        // sense explicació faria pensar que l'app s'ho ha inventat; el que ha passat és que
        // ja ho sabíem de l'historial.
        if (resposta.backfilled > 0) setRecuperats(resposta.backfilled);
        settings.reload();
      });
  };

  if (config === undefined) return null;

  return (
    <div style={{ paddingLeft: 18, display: 'grid', gap: 10 }}>
      <Toggle
        testId={`tracking-on-${scopeId}`}
        label={t('settings.trackingOn')}
        checked={config.time_tracking}
        onChange={(value) => desa({ time_tracking: value })}
      />
      <p style={HINT}>{t('settings.trackingHelp')}</p>
      {recuperats === null ? null : (
        <p
          data-testid={`tracking-backfilled-${scopeId}`}
          style={{ ...HINT, color: 'var(--kicker)' }}
        >
          {t('settings.trackingBackfilled', { count: recuperats })}
        </p>
      )}

      {config.time_tracking ? (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              {t('settings.workHours')}
            </span>
            <input
              type="time"
              className="plou-input"
              data-testid={`work-start-${scopeId}`}
              value={config.work_start}
              onChange={(event) => desa({ work_start: event.target.value })}
              style={{ width: 'auto' }}
            />
            <input
              type="time"
              className="plou-input"
              data-testid={`work-end-${scopeId}`}
              value={config.work_end}
              onChange={(event) => desa({ work_end: event.target.value })}
              style={{ width: 'auto' }}
            />
          </div>

          {/* Els dies laborables, en l'ordre en què es diuen: de dilluns a diumenge. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((dia, index) => (
              <label
                key={dia}
                style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 }}
              >
                <input
                  type="checkbox"
                  data-testid={`work-day-${scopeId}-${dia}`}
                  checked={config.work_days[index] === '1'}
                  onChange={(event) => {
                    const dies = config.work_days.split('');
                    dies[index] = event.target.checked ? '1' : '0';
                    desa({ work_days: dies.join('') });
                  }}
                />
                {t(`settings.day.${dia}`)}
              </label>
            ))}
          </div>

          <Toggle
            testId={`overtime-${scopeId}`}
            label={t('settings.overtimeVisible')}
            checked={config.overtime_visible}
            onChange={(value) => desa({ overtime_visible: value })}
          />

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
            {t('settings.longSession')}
            <input
              type="number"
              className="plou-input"
              data-testid={`long-session-${scopeId}`}
              value={config.long_session_hours}
              min={1}
              max={168}
              onChange={(event) => desa({ long_session_hours: Number(event.target.value) })}
              style={{ width: 80 }}
            />
          </label>
        </>
      ) : null}

      {/*
        **La nomenclatura.** Només canvia la paraula de la interfície: el camp segueix sent
        `project_id` a la base, a l'API i a les tools (regla 3).
      */}
      <Chips
        testId={`project-noun-${scopeId}`}
        value={config.project_noun}
        options={[
          { key: 'project' as const, label: t('settings.noun.project') },
          { key: 'client' as const, label: t('settings.noun.client') },
        ]}
        onChange={(value) => desa({ project_noun: value })}
      />

      <Toggle
        testId={`types-on-${scopeId}`}
        label={t('settings.typesOn')}
        checked={config.task_types_enabled}
        onChange={(value) => desa({ task_types_enabled: value })}
      />

      {config.task_types_enabled ? (
        <>
          <Toggle
            testId={`types-required-${scopeId}`}
            label={t('settings.typesRequired')}
            checked={config.task_type_required}
            onChange={(value) => desa({ task_type_required: value })}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(types.data?.data ?? []).map((type) => (
              <span
                key={type.id}
                data-testid={`task-type-${type.id}`}
                className="plou-tag"
                style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
              >
                {type.name}
                <button
                  type="button"
                  data-testid={`task-type-delete-${type.id}`}
                  onClick={() => {
                    void api.delete(`/api/v1/task-types/${type.id}`).then(() => types.reload());
                  }}
                  style={{ ...LINK_BUTTON, color: 'var(--danger-text)' }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="plou-input"
              data-testid={`task-type-name-${scopeId}`}
              value={nou}
              placeholder={t('settings.typeNew')}
              onChange={(event) => setNou(event.target.value)}
            />
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              data-testid={`task-type-add-${scopeId}`}
              disabled={nou.trim() === ''}
              onClick={() => {
                void api
                  .post('/api/v1/task-types', { scope_id: scopeId, name: nou.trim() })
                  .then(() => {
                    setNou('');
                    types.reload();
                  });
              }}
            >
              {t('nav.create')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** L'explicació petita sota d'un camp: el mateix pes a totes les pantalles. */
const HINT = { fontSize: 12, color: 'var(--ink-soft)', margin: 0 } as const;

const LINK_BUTTON = {
  border: 'none',
  background: 'transparent',
  font: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  color: 'var(--ink-soft)',
} as const;

/**
 * D'on surt un àmbit nou.
 *
 * Les tres portes que es van demanar, i **una sola pantalla**: crear-lo de nou, o
 * sincronitzar-lo amb un que ja existeix —d'aquest servidor, enganxant el token; o d'un
 * altre, enganxant també l'adreça—. Que siguin un commutador i no tres llocs diferents és
 * el que fa que no calgui saber abans quina de les tres es vol.
 */
type ScopeSource = 'new' | 'here' | 'remote';

function ScopesTab() {
  const { scopes } = useSessionData();
  const { reload } = useSession();
  const [source, setSource] = useState<ScopeSource>('new');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'individual' | 'collective'>('individual');
  const [color, setColor] = useState<string>(SCOPE_COLORS[0]);
  const [token, setToken] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation(async () => {
    setError(null);
    try {
      if (source === 'here') {
        // El mateix camí que la pantalla del convit: un token d'aquesta casa es bescanvia
        // i prou, i qui l'accepta ja és membre de l'àmbit que ja hi era.
        if (token.trim() === '') return;
        await api.post(`/api/v1/join/${encodeURIComponent(token.trim())}`);
      } else if (source === 'remote') {
        if (token.trim() === '' || serverUrl.trim() === '') return;
        await api.post('/api/v1/federation/links', {
          base_url: serverUrl.trim(),
          token: token.trim(),
          ...(name.trim() === '' ? {} : { name: name.trim() }),
        });
      } else {
        if (name.trim() === '') return;
        await api.post('/api/v1/scopes', { id: uuidv7(), name: name.trim(), kind, color });
      }
    } catch (problem: unknown) {
      setError(problem instanceof ApiError ? problem.message : String(problem));
      return;
    }
    setName('');
    setToken('');
    setServerUrl('');
    await reload();
  });

  return (
    <>
      <Group title={t('settings.tab.scopes')}>
        {scopes.map((scope) => (
          <ScopeRow key={scope.id} scope={scope} onDone={reload} />
        ))}
      </Group>

      {/*
        **Els projectes viuen aquí, amb els àmbits.**
        Fins ara només es podien crear des del `+` de la barra superior, que és el lloc on
        es va a fer coses i no a configurar-les. Un projecte és estructura, com un àmbit.
      */}
      <Group title={t('settings.projects')}>
        <p style={HINT}>{t('settings.projectsHelp')}</p>
        {scopes.map((scope) => (
          <ProjectsOfScope key={scope.id} scope={scope} />
        ))}
      </Group>

      <Group title={t('settings.newScope')}>
        <Chips
          testId="new-scope-source"
          value={source}
          options={[
            { key: 'new' as const, label: t('settings.scopeSource.new') },
            { key: 'here' as const, label: t('settings.scopeSource.here') },
            { key: 'remote' as const, label: t('settings.scopeSource.remote') },
          ]}
          onChange={setSource}
        />

        {source === 'new' ? (
          <>
            <input
              className="plou-input"
              data-testid="new-scope-name"
              value={name}
              placeholder={t('settings.scopeName')}
              onChange={(event) => setName(event.target.value)}
            />
            <ColorPicker value={color} onChange={setColor} testId="new-scope-color" />
            <Chips
              testId="new-scope-kind"
              value={kind}
              options={[
                { key: 'individual' as const, label: t('settings.scopeKind.individual') },
                { key: 'collective' as const, label: t('settings.scopeKind.collective') },
              ]}
              onChange={setKind}
            />
          </>
        ) : (
          <>
            <p style={HINT}>{t('settings.scopeSourceHelp')}</p>
            {source === 'remote' && (
              <input
                className="plou-input"
                data-testid="new-scope-server"
                value={serverUrl}
                placeholder={t('settings.scopeServerUrl')}
                onChange={(event) => setServerUrl(event.target.value)}
              />
            )}
            <input
              className="plou-input"
              data-testid="new-scope-token"
              value={token}
              placeholder={t('settings.scopeToken')}
              onChange={(event) => setToken(event.target.value)}
            />
          </>
        )}

        {error !== null && (
          <p role="alert" style={{ ...HINT, color: 'var(--danger-text)' }}>
            {error}
          </p>
        )}

        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="new-scope-create"
          disabled={create.busy}
          onClick={() => void create.run()}
        >
          {source === 'new' ? t('nav.create') : t('settings.scopeJoin')}
        </button>
      </Group>
    </>
  );
}

interface ScopeInvite {
  id: string;
  role: string | null;
  max_uses: number;
  use_count: number;
  expires_at: string | null;
  created_at: string;
}

/**
 * Els membres d'un àmbit, i com se n'hi posen.
 *
 * Fins avui això era **estrictament de lectura**: els endpoints de membres existien i no
 * els cridava ningú, i per afegir algú calia saber-ne l'identificador d'usuari i fer-ho a
 * mà. Ara hi ha el convit, que és el camí que fa que compartir sigui una cosa que es pugui
 * fer sense mirar la base de dades.
 */
function MembersList({ scopeId, canManage }: { scopeId: string; canManage: boolean }) {
  const members = useApi<Member[]>(`/api/v1/scopes/${scopeId}/members`);
  const invites = useApi<ScopeInvite[]>(canManage ? `/api/v1/scopes/${scopeId}/invites` : null);
  const [role, setRole] = useState<'collaborator' | 'viewer'>('collaborator');
  const [fresh, setFresh] = useState<string | null>(null);

  const invite = useMutation(async () => {
    const created = await api.post<{ invite_url: string }>(`/api/v1/scopes/${scopeId}/invites`, {
      role,
    });
    // **Surt una sola vegada.** Del hash no es pot recuperar (docs/10 §6).
    setFresh(created.invite_url);
    invites.reload();
  });

  return (
    <div style={{ paddingLeft: 18, display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gap: 5 }}>
        {(members.data ?? []).map((member) => (
          <div
            key={member.id}
            data-testid={`member-${member.id}`}
            style={{ fontSize: 12.5, color: 'var(--ink-soft)', display: 'flex', gap: 8 }}
          >
            <span>{member.name ?? member.user_id ?? ''}</span>

            {/*
              **El rol es canvia aquí**, i fins avui no es podia: el `PATCH` existia des del
              primer dia i no el cridava ningú, o sigui que l'única manera d'elevar algú era
              fer-lo propietari —i llavors també podia esborrar l'àmbit sencer.

              El propietari no es toca des d'aquí: treure-se'l a un mateix per descuit deixa
              l'àmbit sense qui el governi, i el servidor ja ho impedeix quan és l'últim.
            */}
            {canManage && member.role !== 'owner' ? (
              <select
                className="plou-input"
                data-testid={`member-role-${member.id}`}
                value={member.role}
                onChange={(event) => {
                  void api
                    .patch(`/api/v1/scopes/${scopeId}/members/${member.id}`, {
                      role: event.target.value,
                    })
                    .then(() => members.reload());
                }}
                style={{ width: 'auto', fontSize: 12, padding: '2px 8px' }}
              >
                {(['admin', 'collaborator', 'viewer'] as const).map((role) => (
                  <option key={role} value={role}>
                    {t(`settings.role.${role}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span style={{ color: 'var(--ink-faint)' }}>{t(`settings.role.${member.role}`)}</span>
            )}

            {canManage && member.role !== 'owner' ? (
              <button
                type="button"
                data-testid={`member-remove-${member.id}`}
                style={LINK_BUTTON}
                onClick={() => {
                  void api
                    .delete(`/api/v1/scopes/${scopeId}/members/${member.id}`)
                    .then(() => members.reload());
                }}
              >
                {t('settings.memberRemove')}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {canManage ? (
        <>
          <Chips
            testId={`invite-role-${scopeId}`}
            value={role}
            options={[
              { key: 'collaborator' as const, label: t('settings.role.collaborator') },
              { key: 'viewer' as const, label: t('settings.role.viewer') },
            ]}
            onChange={setRole}
          />
          <button
            type="button"
            className="plou-btn"
            data-testid={`invite-create-${scopeId}`}
            disabled={invite.busy}
            onClick={() => void invite.run()}
          >
            {t('settings.inviteCreate')}
          </button>

          {fresh === null ? null : (
            <div style={{ display: 'grid', gap: 4 }}>
              <code
                data-testid={`invite-url-${scopeId}`}
                style={{
                  fontSize: 11.5,
                  background: 'var(--code-bg)',
                  padding: '6px 8px',
                  borderRadius: 8,
                  wordBreak: 'break-all',
                }}
              >
                {fresh}
              </code>
              <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
                {t('settings.inviteOnce')}
              </span>
            </div>
          )}

          {(invites.data ?? []).length === 0 ? null : (
            <div style={{ display: 'grid', gap: 4 }}>
              {(invites.data ?? []).map((row) => (
                <div key={row.id} style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
                  {t(`settings.role.${row.role ?? 'collaborator'}`)} · {row.use_count}/
                  {row.max_uses}{' '}
                  <button
                    type="button"
                    style={LINK_BUTTON}
                    data-testid={`invite-revoke-${row.id}`}
                    onClick={() => {
                      void api
                        .delete(`/api/v1/scopes/${scopeId}/invites/${row.id}`)
                        .then(() => invites.reload());
                    }}
                  >
                    {t('settings.inviteRevoke')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * Un calendari d'un àmbit, i si els membres el veuen.
 *
 * **L'avís de credencials no és decoratiu.** El secret d'una font externa està segellat
 * amb una clau derivada del secret d'aquesta instància: no pot viatjar enlloc. El que sí
 * que passa, i no és obvi, és que si la font és bidireccional el que escriguin els
 * membres anirà al servei d'un tercer **amb les credencials del propietari**.
 */
function SharedCalendarRow({
  calendar,
  collective,
  onDone,
}: {
  calendar: Calendar;
  collective: boolean;
  onDone: () => void;
}) {
  const shared = calendar.shared_with_scope === true;

  const toggle = (): void => {
    if (!shared && calendar.has_credentials === true) {
      if (!window.confirm(t('settings.calendarCredWarning'))) return;
    }
    void api.patch(`/api/v1/calendars/${calendar.id}`, { shared_with_scope: !shared }).then(onDone);
  };

  return (
    <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', display: 'flex', gap: 8 }}>
      <span>
        {calendar.name} ·{' '}
        {calendar.kind === 'todos' ? t('settings.caldavTodos') : t('settings.caldavEvents')}
      </span>
      {/*
        Només té sentit a un àmbit col·lectiu: a un d'individual no hi ha ningú amb qui
        compartir, i un commutador que no fa res convida a pensar que sí que en fa.
      */}
      {collective ? (
        <button
          type="button"
          data-testid={`calendar-share-${calendar.id}`}
          style={LINK_BUTTON}
          onClick={toggle}
        >
          {shared ? t('settings.calendarShared') : t('settings.calendarPrivate')}
        </button>
      ) : null}
    </div>
  );
}

function CalendarsTab() {
  const { scopes } = useSessionData();
  const calendars = useApi<Calendar[]>('/api/v1/calendars');
  const base = window.location.origin;

  return (
    <>
      {scopes.map((scope) => {
        const own = (calendars.data ?? []).filter((calendar) => calendar.scope_id === scope.id);
        return (
          <Group key={scope.id} title={scope.name}>
            {own.length === 0 ? (
              <EmptyState>{t('settings.empty.calendars')}</EmptyState>
            ) : (
              own.map((calendar) => (
                <SharedCalendarRow
                  key={calendar.id}
                  calendar={calendar}
                  collective={scope.kind === 'collective'}
                  onDone={() => calendars.reload()}
                />
              ))
            )}

            <SourcesForScope scope={scope} calendars={calendars} />

            {/*
              Cada àmbit en publica DUES (D9): esdeveniments i tasques. Han d'estar
              etiquetades perquè s'entengui quina és quina; una llista de dues URL
              gairebé iguals sense etiqueta és una invitació a triar la que no toca.
            */}
            <div style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
                {t('settings.caldavUrls')}
              </span>
              {(['events', 'todos'] as const).map((kind) => (
                <div key={kind} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-faint)', minWidth: 90 }}>
                    {kind === 'todos' ? t('settings.caldavTodos') : t('settings.caldavEvents')}
                  </span>
                  <input
                    readOnly
                    data-testid={`caldav-${scope.id}-${kind}`}
                    value={`${base}/dav/calendars/${scope.id}-${kind}/`}
                    onFocus={(event) => event.currentTarget.select()}
                    className="plou-input"
                    style={{ fontSize: 11.5 }}
                  />
                  {/*
                    El botó de copiar que `docs/02` §5 demana. Seleccionar-la en enfocar-la
                    ajuda i no és el mateix: aquesta URL s'enganxa a una altra aplicació, i
                    fer-ho amb el teclat vol dir encertar el camp, `Cmd+C` i confiar-hi.
                  */}
                  <CopyButton value={`${base}/dav/calendars/${scope.id}-${kind}/`} />
                </div>
              ))}
            </div>
          </Group>
        );
      })}
    </>
  );
}

/**
 * Les fonts de dades d'un àmbit: CalDAV, iCal o RSS.
 *
 * `docs/07` §9 ja preveu un CalDAV o un `.ics` com a origen; el disseny validat hi
 * afegeix l'RSS. Els tres s'afegeixen igual i es veuen al calendari, on cadascú els pot
 * apagar sense treure'ls a ningú.
 *
 * **La contrasenya no es torna a ensenyar mai.** El camp es queda buit en carregar i
 * enviar-lo buit vol dir "no la toquis": desar el nom d'una font no ha de perdre'n les
 * credencials, i tornar-la a pintar la posaria al DOM de qualsevol pestanya oberta.
 */
function SourcesForScope({
  scope,
  calendars,
}: {
  scope: Scope;
  calendars: ReturnType<typeof useApi<Calendar[]>>;
}) {
  const [kind, setKind] = useState<'caldav' | 'ical' | 'rss'>('ical');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sources = (calendars.data ?? []).filter(
    (calendar) => calendar.scope_id === scope.id && calendar.origin === 'subscription',
  );

  const add = useMutation(async () => {
    if (url.trim() === '') {
      setError(t('settings.sources.urlRequired'));
      return;
    }
    setError(null);
    await api.post('/api/v1/calendars', {
      id: uuidv7(),
      scope_id: scope.id,
      name: name.trim() === '' ? url.trim() : name.trim(),
      kind: 'events',
      origin: 'subscription',
      source_kind: kind,
      source_url: url.trim(),
      source_username: kind === 'caldav' && username !== '' ? username : undefined,
      source_secret: kind === 'caldav' && password !== '' ? password : undefined,
    });
    setName('');
    setUrl('');
    setUsername('');
    setPassword('');
    calendars.reload();
  });

  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid={`sources-${scope.id}`}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
        {t('settings.sources')}
      </span>

      {sources.length === 0 ? (
        <EmptyState>{t('settings.sources.empty')}</EmptyState>
      ) : (
        sources.map((source) => (
          <div
            key={source.id}
            data-testid={`source-${source.id}`}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '8px 10px',
              borderRadius: 12,
              background: 'var(--tag-bg)',
            }}
          >
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-soft)' }}>
              {t(`settings.sources.kind.${source.source_kind ?? 'ical'}`)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>
                {source.name}
              </div>
              {/*
                L'estat de l'últim refresc. Una font caiguda es veu exactament igual que
                una que no té esdeveniments —buida—, i sense dir-ho aquí ningú se
                n'assabenta fins que troba a faltar alguna cosa.
              */}
              <div
                style={{
                  fontSize: 11,
                  color: source.last_error == null ? 'var(--ink-faint)' : 'var(--danger-text)',
                }}
              >
                {source.last_error != null
                  ? t('settings.sources.failed', { reason: source.last_error })
                  : source.last_refreshed_at == null
                    ? t('settings.sources.never')
                    : t('settings.sources.refreshed', {
                        when: dateTime(getLocale(), new Date(source.last_refreshed_at)),
                      })}
              </div>
            </div>
            {/*
              A la bústia o només al calendari.
              **Es desa l'excepció i no l'estat**: si el valor triat coincideix amb el que
              el defecte ja diria, s'envia `null` i la base es queda buida. Així el dia
              que el defecte canviï, qui no hagi tocat res el segueix. La casella, en
              canvi, és un interruptor normal: ensenya `inbox_visible ?? el defecte`.
            */}
            <Toggle
              checked={source.inbox_visible ?? source.inbox_visible_default}
              label={t('settings.sources.inbox')}
              testId={`source-inbox-${source.id}`}
              onChange={(next) => {
                void api
                  .patch(`/api/v1/calendars/${source.id}`, {
                    inbox_visible: next === source.inbox_visible_default ? null : next,
                  })
                  .then(() => calendars.reload());
              }}
            />
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              data-testid={`source-remove-${source.id}`}
              onClick={() => {
                void api.delete(`/api/v1/calendars/${source.id}`).then(() => calendars.reload());
              }}
              style={{ fontSize: 11.5, padding: '4px 10px' }}
            >
              {t('settings.sources.remove')}
            </button>
          </div>
        ))
      )}

      <Chips
        testId={`source-kind-${scope.id}`}
        value={kind}
        options={[
          { key: 'caldav' as const, label: t('settings.sources.kind.caldav') },
          { key: 'ical' as const, label: t('settings.sources.kind.ical') },
          { key: 'rss' as const, label: t('settings.sources.kind.rss') },
        ]}
        onChange={setKind}
      />
      <input
        className="plou-input"
        data-testid={`source-name-${scope.id}`}
        placeholder={t('settings.sources.name')}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <input
        className="plou-input"
        data-testid={`source-url-${scope.id}`}
        placeholder={t('settings.sources.url')}
        value={url}
        onChange={(event) => setUrl(event.target.value)}
      />
      {/* Usuari i contrasenya només tenen sentit amb CalDAV: un `.ics` publicat i un RSS
          es baixen sense credencials. */}
      {kind === 'caldav' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="plou-input"
            data-testid={`source-user-${scope.id}`}
            placeholder={t('settings.sources.username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <input
            className="plou-input"
            type="password"
            data-testid={`source-pass-${scope.id}`}
            placeholder={t('settings.sources.password')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
      ) : null}
      {error === null ? null : (
        <span style={{ fontSize: 11, color: 'var(--danger-text)' }}>{error}</span>
      )}
      <button
        type="button"
        className="plou-btn plou-btn-primary"
        data-testid={`source-add-${scope.id}`}
        disabled={add.busy}
        onClick={() => void add.run()}
        style={{ justifySelf: 'start' }}
      >
        {t('settings.sources.add')}
      </button>
    </div>
  );
}

function AiTab() {
  const agents = useApi<Agent[]>('/api/v1/ai/agents');
  const [name, setName] = useState('');
  /**
   * **Un canvi en un agent en canvia un altre.** Assignar «Feina» a l'Hermes fa que la
   * casella del Codex hagi de sortir desactivada, i la disponibilitat la sap el servidor:
   * aquest comptador la fa tornar a demanar a **totes** les files, no només a la que s'ha
   * tocat. Sense això, la casella presa es podria marcar fins que algú recarregués.
   */
  const [versio, setVersio] = useState(0);

  return (
    <Group title={t('settings.agents')}>
      {(agents.data ?? []).length === 0 ? (
        <EmptyState>{t('settings.empty.agents')}</EmptyState>
      ) : (
        (agents.data ?? []).map((agent) => (
          <AgentRow
            key={agent.id}
            agent={agent}
            versio={versio}
            onChanged={() => {
              agents.reload();
              setVersio((value) => value + 1);
            }}
          />
        ))
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="plou-input"
          data-testid="agent-name"
          value={name}
          placeholder={t('settings.newAgent')}
          onChange={(event) => setName(event.target.value)}
        />
        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="agent-create"
          disabled={name.trim() === ''}
          onClick={() => {
            void api.post('/api/v1/ai/agents', { id: uuidv7(), name: name.trim() }).then(() => {
              setName('');
              agents.reload();
            });
          }}
        >
          {t('nav.create')}
        </button>
      </div>
    </Group>
  );
}

/**
 * La configuració d'MCP, feta.
 *
 * Amb el testimoni sencer si s'acaba de crear, i amb un forat si no: **del hash no se'n pot
 * treure**, i un fitxer que sembla complet i porta una credencial inventada faria perdre
 * mitja tarda a qui l'enganxi.
 */
function mcpJson(token: string | null): string {
  return `${JSON.stringify(
    {
      mcpServers: {
        'fem-ho': {
          type: 'http',
          url: `${window.location.origin}/mcp`,
          headers: { Authorization: `Bearer ${token ?? 'ENGANXA-HI-LA-CREDENCIAL'}` },
        },
      },
    },
    null,
    2,
  )}\n`;
}

/** Baixar un text com a fitxer, sense passar pel servidor: ja el tenim aquí. */
function baixa(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Un agent: què pot fer, **d'on agafa feina**, i amb què s'hi connecta.
 *
 * Els tres blocs són el que cal per posar-lo a treballar, i van junts perquè és una sola
 * decisió: qui és, què porta i com hi entra. Repartits per pantalles, configurar un agent
 * seria anar i tornar tres vegades.
 */
function AgentRow({
  agent,
  versio,
  onChanged,
}: {
  agent: Agent;
  versio: number;
  onChanged: () => void;
}) {
  const { scopes } = useSessionData();
  const disponibilitat = useApi<{
    data: { scope_id: string; taken_by: { name: string } | null }[];
  }>(`/api/v1/ai/agents/${agent.id}/scope-availability`, [versio]);
  const credencials = useApi<{ data: ApiTokenSummary[] }>(
    `/api/v1/ai/agents/${agent.id}/credentials`,
  );
  const [nova, setNova] = useState<string | null>(null);
  const [newCredential, setNewCredential] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presa = (scopeId: string): string | null =>
    disponibilitat.data?.data.find((row) => row.scope_id === scopeId)?.taken_by?.name ?? null;

  /** Desa el conjunt sencer: la pantalla envia les caselles tal com han quedat. */
  const desa = (next: { scope_ids: string[]; all_scopes: boolean }): void => {
    setError(null);
    void api
      .put(`/api/v1/ai/agents/${agent.id}/scopes`, next)
      .then(onChanged)
      .catch((cause: unknown) => setError(failureText(cause)));
  };

  return (
    <div
      data-testid={`agent-${agent.id}`}
      style={{
        display: 'grid',
        gap: 10,
        padding: 12,
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
        <span style={{ fontWeight: 600 }}>{agent.name}</span>
        <Toggle
          testId={`agent-enabled-${agent.id}`}
          label={t('settings.agentEnabled')}
          checked={agent.enabled}
          onChange={(value) => {
            void api.patch(`/api/v1/ai/agents/${agent.id}`, { enabled: value }).then(onChanged);
          }}
        />
        <Toggle
          testId={`agent-create-${agent.id}`}
          label={t('settings.agentCanCreate')}
          checked={agent.can_create_tasks}
          onChange={(value) => {
            void api
              .patch(`/api/v1/ai/agents/${agent.id}`, { can_create_tasks: value })
              .then(onChanged);
          }}
        />
      </div>

      {/*
        **D'on agafa feina.** Un àmbit té un sol agent: els que ja té un altre surten
        desactivats **amb el seu nom**, perquè saber a qui anar és el següent pas i deixar
        marcar per respondre amb un error després no ho és.
      */}
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
          {t('settings.agentScopes')}
        </span>
        <Toggle
          testId={`agent-all-scopes-${agent.id}`}
          label={t('settings.agentAllScopes')}
          checked={agent.all_scopes}
          onChange={(value) => desa({ scope_ids: value ? [] : agent.scope_ids, all_scopes: value })}
        />
        {agent.all_scopes ? null : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {scopes.map((scope) => {
              const altre = presa(scope.id);
              return (
                <label
                  key={scope.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    color: altre === null ? 'var(--ink)' : 'var(--ink-faint)',
                  }}
                  title={
                    altre === null ? undefined : t('settings.agentScopeTaken', { name: altre })
                  }
                >
                  <input
                    type="checkbox"
                    data-testid={`agent-scope-${agent.id}-${scope.id}`}
                    checked={agent.scope_ids.includes(scope.id)}
                    disabled={altre !== null}
                    onChange={(event) =>
                      desa({
                        all_scopes: false,
                        scope_ids: event.target.checked
                          ? [...agent.scope_ids, scope.id]
                          : agent.scope_ids.filter((id) => id !== scope.id),
                      })
                    }
                  />
                  {scope.name}
                  {altre === null ? null : ` · ${altre}`}
                </label>
              );
            })}
          </div>
        )}
        {error === null ? null : (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--danger-text)' }}>{error}</p>
        )}
      </div>

      {/*
        **Amb què s'hi connecta.** El testimoni surt una sola vegada —del hash no se'n pot
        treure— i per això va amb el botó de copiar (P17) i amb l'avís al costat.
      */}
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
          {t('settings.agentCredentials')}
        </span>
        {(credencials.data?.data ?? []).map((cred) => (
          <div
            key={cred.id}
            style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}
          >
            <span style={{ fontFamily: 'monospace' }}>{cred.token_prefix}…</span>
            <span style={{ color: 'var(--ink-faint)' }}>{cred.name}</span>
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              data-testid={`agent-credential-revoke-${cred.id}`}
              onClick={() => {
                void api.delete(`/api/v1/tokens/${cred.id}`).then(() => credencials.reload());
              }}
              style={{ fontSize: 11.5, color: 'var(--danger-text)' }}
            >
              {t('tokens.revoke')}
            </button>
          </div>
        ))}

        {nova === null ? null : (
          <div style={{ display: 'grid', gap: 5 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                readOnly
                className="plou-input"
                data-testid={`agent-credential-value-${agent.id}`}
                value={nova}
                onFocus={(event) => event.currentTarget.select()}
                style={{ fontSize: 12, flex: 1 }}
              />
              <CopyButton value={nova} />
            </div>
            <p style={{ margin: 0, fontSize: 11.5, color: 'var(--danger-text)' }}>
              {t('tokens.onceWarning')}
            </p>
          </div>
        )}

        {/*
          **Com s'hi connecta**, i quin dels dos fitxers és el secret.

          Són dos a posta: la configuració d'MCP **porta la credencial** —qui la tingui pot
          llegir i escriure les teves tasques— i el full d'instruccions no en porta cap. Amb
          un sol fitxer, «això es pot passar per un xat?» no tindria resposta.
        */}
        <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
            {t('settings.agentConnect')}
          </span>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              readOnly
              className="plou-input"
              data-testid={`agent-mcp-url-${agent.id}`}
              value={`${window.location.origin}/mcp`}
              onFocus={(event) => event.currentTarget.select()}
              style={{ fontSize: 12 }}
            />
            <CopyButton value={`${window.location.origin}/mcp`} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <CopyButton value={mcpJson(nova)} />
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              data-testid={`agent-mcp-download-${agent.id}`}
              onClick={() => baixa('mcp.json', mcpJson(nova), 'application/json')}
              style={{ fontSize: 11.5 }}
            >
              {t('settings.agentDownloadMcp')}
            </button>
            <span style={{ fontSize: 11.5, color: 'var(--danger-text)', fontWeight: 600 }}>
              <span aria-hidden="true">⚠ </span>
              {nova === null ? t('settings.agentMcpNoToken') : t('settings.agentMcpHasToken')}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="plou-btn plou-btn-ghost"
              data-testid={`agent-skill-download-${agent.id}`}
              onClick={() => {
                void api
                  .text('/api/v1/ai/skill')
                  .then((text) => baixa('fem-ho.skill.md', text, 'text/markdown'));
              }}
              style={{ fontSize: 11.5 }}
            >
              {t('settings.agentDownloadSkill')}
            </button>
            <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
              {t('settings.agentSkillNoToken')}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="plou-btn plou-btn-ghost"
          data-testid={`agent-credential-new-${agent.id}`}
          onClick={() => setNewCredential(true)}
          style={{ fontSize: 12, width: 'fit-content' }}
        >
          {t('settings.agentNewCredential')}
        </button>
        {newCredential ? (
          <AgentTokenEditor
            agentId={agent.id}
            scopeIds={agent.scope_ids}
            allScopes={agent.all_scopes}
            onCancel={() => setNewCredential(false)}
            onSaved={(result) => {
              setNova(result.token ?? null);
              setNewCredential(false);
              credencials.reload();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function SharesTab() {
  const shares = useApi<{ data: ShareSummary[] }>('/api/v1/shares');

  return (
    <Group title={t('settings.tab.shares')}>
      {(shares.data?.data ?? []).length === 0 ? (
        <EmptyState>{t('settings.empty.shares')}</EmptyState>
      ) : (
        (shares.data?.data ?? []).map((share) => (
          <div key={share.id} style={{ display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
              <span style={{ fontWeight: 600 }}>{t(`share.permission.${share.permission}`)}</span>
              <span style={{ color: 'var(--ink-faint)' }}>
                {share.view_count} · {share.revoked_at === null ? '' : t('share.revoked')}
              </span>
              {share.revoked_at === null ? (
                <button
                  type="button"
                  data-testid={`share-revoke-${share.id}`}
                  onClick={() => {
                    void api.delete(`/api/v1/shares/${share.id}`).then(() => {
                      shares.reload();
                    });
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    font: 'inherit',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: 'var(--danger-text)',
                  }}
                >
                  {t('share.revoke')}
                </button>
              ) : null}
            </div>
            <ShareAccesses shareId={share.id} />
          </div>
        ))
      )}
    </Group>
  );
}

function ShareAccesses({ shareId }: { shareId: string }) {
  const accesses = useApi<{ data: { id: string; label: string; last_seen: string }[] }>(
    `/api/v1/shares/${shareId}/accesses`,
  );
  const rows = accesses.data?.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div style={{ paddingLeft: 14, display: 'grid', gap: 3 }}>
      {rows.map((access) => (
        <span key={access.id} style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
          {/* Pseudònim sempre: no hi ha cap columna d'IP enlloc (D10). */}
          {access.label} · {access.last_seen.slice(0, 10)}
        </span>
      ))}
    </div>
  );
}

function ProfileTab() {
  const { profile, settings } = useSessionData();
  const { updateProfile, updateSettings } = useSession();
  const [name, setName] = useState(profile.name);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);
  const [gravatarSays, setGravatarSays] = useState<string | null>(null);

  /**
   * Omplir el nom amb el que hi ha a Gravatar.
   *
   * **Es proposa, no s'aplica sol.** Sobreescriure el nom que algú ha escrit aquí amb el
   * que va posar fa cinc anys en un altre lloc és canviar-li les dades sense demanar-ho;
   * per això és un botó i no una sincronització.
   */
  const fill = useMutation(async () => {
    const found = await api.get<{ display_name: string | null } | null>('/api/v1/me/gravatar');
    if (found?.display_name == null || found.display_name === '') {
      setGravatarSays(t('settings.gravatarNothing'));
      return;
    }
    setGravatarSays(null);
    setName(found.display_name);
    await updateProfile({ name: found.display_name });
  });

  const change = useMutation(async () => {
    await api.post('/api/v1/auth/password', {
      current_password: current,
      new_password: next,
    });
    setCurrent('');
    setNext('');
    setDone(true);
  });

  return (
    <>
      {/* El brief és explícit (línia 42): aquí NO s'editen els altres. */}
      <Group title={t('settings.tab.profile')}>
        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
            {t('settings.profileName')}
          </span>
          <input
            className="plou-input"
            data-testid="profile-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() !== '' && name !== profile.name) void updateProfile({ name });
            }}
          />
        </label>
        <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>{profile.email ?? ''}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>{profile.timezone}</div>
      </Group>

      <Group title="Gravatar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar userId={profile.id} name={profile.name} size={48} />
          <div style={{ display: 'grid', gap: 6 }}>
            <Toggle
              label={t('settings.gravatar')}
              testId="settings-gravatar"
              checked={settings.gravatar !== false}
              onChange={(value) => void updateSettings({ gravatar: value })}
            />
          </div>
        </div>

        {/*
          **Es diu què costa, i es diu bé.** "Només s'envia un hash" es llegeix sovint i no
          és cap protecció: per a una adreça que algú ja sospita, comprovar-la és calcular
          el hash i comparar.
        */}
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-faint)', lineHeight: 1.5 }}>
          {t('settings.gravatarHelp')}
        </p>

        {gravatarSays !== null ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>{gravatarSays}</p>
        ) : null}

        <button
          type="button"
          className="plou-btn"
          data-testid="settings-gravatar-fill"
          disabled={fill.busy || settings.gravatar === false}
          onClick={() => void fill.run()}
        >
          {t('settings.gravatarFill')}
        </button>
      </Group>

      <Group title={t('settings.changePassword')}>
        <input
          className="plou-input"
          type="password"
          data-testid="password-current"
          value={current}
          placeholder={t('settings.currentPassword')}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <input
          className="plou-input"
          type="password"
          data-testid="password-new"
          value={next}
          placeholder={t('settings.newPassword')}
          onChange={(event) => setNext(event.target.value)}
        />
        {change.error !== undefined ? (
          <p role="alert" style={{ margin: 0, fontSize: 12, color: 'var(--danger-text)' }}>
            {change.error.message}
          </p>
        ) : null}
        {done ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
            {t('settings.passwordChanged')}
          </p>
        ) : null}
        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="password-submit"
          disabled={change.busy}
          onClick={() => void change.run()}
        >
          {t('nav.save')}
        </button>
      </Group>
    </>
  );
}

function AdminTab() {
  const { instance } = useSessionData();
  const [brandError, setBrandError] = useState<string | null>(null);
  /** Amb el logo posat al `.env`, la pujada no serveix de res i es diu en comptes d'oferir-la. */
  const fixatPelEntorn =
    instance.logo_url !== null && !String(instance.logo_url ?? '').startsWith('/brand/');
  const users = useApi<AdminUser[]>('/api/v1/admin/users');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [invite, setInvite] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');

  /**
   * **El nom de la instància, per dir-lo abans que calgui escriure'l.**
   *
   * Es demanava `/info` **dues vegades**: una aquí, amb el resultat llençat, i una altra
   * en enfocar el camp. Fins que no hi clicaves, el marcador deia literalment «Escriu «»
   * per confirmar» —amb el nom buit—, o sigui que la instrucció que et diu què has
   * d'escriure no ho deia. I el servidor exigeix el nom exacte: sense saber-lo, l'acció
   * no es pot fer, no és que sigui perillosa.
   */
  const info = useApi<{ name: string }>('/info');
  const instanceName = info.data?.name ?? '';

  const send = useMutation(async () => {
    const result = await api.post<{ invite_url: string }>('/api/v1/admin/users/invite', {
      email,
      name,
    });
    setInvite(result.invite_url);
    setEmail('');
    setName('');
    users.reload();
  });

  const wipe = useMutation(async () => {
    await api.post('/api/v1/admin/wipe', { confirmation });
    setConfirmation('');
    window.location.assign('/');
  });

  return (
    <>
      <Group title={t('settings.users')}>
        {users.error !== undefined ? <ErrorBanner onRetry={users.reload} /> : null}
        {(users.data ?? []).map((user) => (
          <div
            key={user.id}
            style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}
          >
            <span style={{ fontWeight: 600 }}>{user.name}</span>
            <span style={{ color: 'var(--ink-faint)' }}>{user.email ?? ''}</span>
            <span style={{ color: 'var(--ink-faint)' }}>{t(`settings.role.${user.role}`)}</span>
            {user.pending_invite ? (
              <span style={{ color: 'var(--ink-faint)' }}>{t('settings.invitePending')}</span>
            ) : null}
          </div>
        ))}
      </Group>

      <Group title={t('settings.invite')}>
        <input
          className="plou-input"
          data-testid="invite-email"
          value={email}
          placeholder={t('settings.inviteEmail')}
          onChange={(event) => setEmail(event.target.value)}
        />
        <input
          className="plou-input"
          data-testid="invite-name"
          value={name}
          placeholder={t('settings.inviteName')}
          onChange={(event) => setName(event.target.value)}
        />
        {invite !== null ? (
          <input
            readOnly
            data-testid="invite-url"
            value={invite}
            onFocus={(event) => event.currentTarget.select()}
            className="plou-input"
            style={{ fontSize: 11.5 }}
          />
        ) : null}
        {send.error !== undefined ? (
          <p role="alert" style={{ margin: 0, fontSize: 12, color: 'var(--danger-text)' }}>
            {send.error.message}
          </p>
        ) : null}
        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="invite-send"
          disabled={send.busy}
          onClick={() => void send.run()}
        >
          {t('settings.invite')}
        </button>
      </Group>

      {/*
        **La marca de la instància.**

        Una eina autoallotjada que es desplega dins d'una empresa s'hi ha de poder
        presentar. El nom ja el porta `FEMHO_INSTANCE_NAME`; això és el logo, que és el que
        de debò la fa semblar seva.

        Amb `FEMHO_LOGO_URL` posat, aquí es diu i no es deixa tocar: qui desplega amb un
        `compose.yaml` immutable no vol que ningú l'hi canviï des de la pantalla.
      */}
      <Group title={t('settings.branding')}>
        {instance.logo_url === null || instance.logo_url === undefined ? (
          <EmptyState>{t('settings.branding.none')}</EmptyState>
        ) : (
          <img
            src={instance.logo_url}
            alt={instance.name}
            data-testid="branding-preview"
            style={{ height: 28, width: 'auto', maxWidth: 220 }}
          />
        )}

        {fixatPelEntorn ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
            {t('settings.branding.fixed')}
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              data-testid="branding-file"
              accept="image/svg+xml,image/png,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file === undefined) return;
                void api
                  .upload('/api/v1/admin/branding/logo', file, { asImage: true })
                  .then(() => window.location.reload())
                  .catch((cause: unknown) => setBrandError(failureText(cause)));
              }}
              style={{ fontSize: 12 }}
            />
            {instance.logo_url === null || instance.logo_url === undefined ? null : (
              <button
                type="button"
                className="plou-btn plou-btn-ghost"
                data-testid="branding-remove"
                onClick={() => {
                  void api
                    .delete('/api/v1/admin/branding/logo')
                    .then(() => window.location.reload());
                }}
                style={{ fontSize: 12 }}
              >
                {t('settings.branding.remove')}
              </button>
            )}
          </div>
        )}
        {brandError === null ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
            {t('settings.branding.help')}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--danger-text)' }}>{brandError}</p>
        )}
      </Group>

      <Group title={t('settings.wipe')}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--danger-text)', lineHeight: 1.5 }}>
          {t('settings.wipeWarning')}
        </p>
        <input
          className="plou-input"
          data-testid="wipe-confirmation"
          value={confirmation}
          placeholder={t('settings.wipeConfirm', { name: instanceName })}
          onChange={(event) => setConfirmation(event.target.value)}
        />
        <button
          type="button"
          data-testid="wipe-submit"
          /*
            **Amb el nom exacte, no amb qualsevol text.** El servidor ja ho exigeix i
            tornava un 422; deixar prémer per rebre'l és fer el camí llarg a qui s'ha
            equivocat, quan la pantalla ja sap la resposta.
          */
          disabled={wipe.busy || instanceName === '' || confirmation !== instanceName}
          onClick={() => void wipe.run()}
          style={{
            padding: '9px 16px',
            borderRadius: 100,
            border: '1px solid var(--danger-text)',
            background: 'transparent',
            color: 'var(--danger-text)',
            font: 'inherit',
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            justifySelf: 'start',
          }}
        >
          {t('settings.wipe')}
        </button>
      </Group>
    </>
  );
}

/**
 * El correu com a font d'entrada.
 *
 * Tres coses d'aquesta pantalla no són decoració:
 *
 * - **La contrasenya no es torna a ensenyar mai.** El camp neix buit i enviar-lo buit vol
 *   dir «no la toquis»: tornar-la a pintar la posaria al DOM de qualsevol pestanya oberta,
 *   i desar el nom del compte no ha de perdre'n les credencials.
 * - **«Prova la connexió» no desa res.** És el botó que estalvia les hores que es perden
 *   quan l'única manera de saber si unes credencials van bé és desar-les i esperar el
 *   cicle següent. Per això es pot provar una contrasenya **abans** de desar-la.
 * - **La previsualització del títol és en viu i la fa la mateixa funció que el servidor**
 *   (`renderMailTitle`, als contractes). Si fossin dues, un dia el que veus escrivint no
 *   seria el que et surt al tauler.
 *
 * I dues frases que hi són perquè la gent ha de saber-ho sense preguntar: **la primera
 * lectura d'una carpeta no ingereix res** i **res marca els teus correus com a llegits**.
 */
function MailTab() {
  const accounts = useApi<MailAccount[]>('/api/v1/mail/accounts');
  const rules = useApi<MailRule[]>('/api/v1/mail/rules');

  return (
    <>
      <Group title={t('settings.mail.accounts')}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          {t('settings.mail.intro')}
        </p>

        {(accounts.data ?? []).length === 0 ? (
          <EmptyState>{t('settings.mail.empty')}</EmptyState>
        ) : (
          (accounts.data ?? []).map((account) => (
            <MailAccountRow
              key={account.id}
              account={account}
              onDone={() => {
                accounts.reload();
                rules.reload();
              }}
            />
          ))
        )}

        <GoogleMailPanel accounts={accounts.data ?? []} onDone={() => accounts.reload()} />
        <MailAccountForm onDone={() => accounts.reload()} />
      </Group>

      <Group title={t('settings.mail.rules')}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          {t('settings.mail.firstRun')} {t('settings.mail.notTouched')}
        </p>

        {(rules.data ?? []).length === 0 ? (
          <EmptyState>{t('settings.mail.rules.empty')}</EmptyState>
        ) : (
          (rules.data ?? []).map((rule) => (
            <MailRuleRow key={rule.id} rule={rule} onDone={() => rules.reload()} />
          ))
        )}

        {(accounts.data ?? []).length > 0 ? (
          <MailRuleForm accounts={accounts.data ?? []} onDone={() => rules.reload()} />
        ) : null}
      </Group>
    </>
  );
}

/**
 * Copiar un text al porta-retalls, i **dir que s'ha copiat**.
 *
 * Sense la confirmació el botó no dona cap senyal —el porta-retalls no es veu— i la gent
 * el prem tres vegades per si de cas. Torna a «Copia» sol perquè un botó que es queda dient
 * «Copiat» menteix la propera vegada que el mires.
 */
function CopyButton({ value }: { value: string }) {
  const [copiat, setCopiat] = useState(false);

  useEffect(() => {
    if (!copiat) return undefined;
    const timer = setTimeout(() => setCopiat(false), 1800);
    return () => clearTimeout(timer);
  }, [copiat]);

  return (
    <button
      type="button"
      className="plou-btn plou-btn-ghost"
      data-testid="copy-button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => setCopiat(true));
      }}
      style={{ fontSize: 11.5, flexShrink: 0 }}
    >
      {copiat ? t('tokens.copied') : t('tokens.copy')}
    </button>
  );
}

const camp = { display: 'grid', gap: 3, fontSize: 11.5, color: 'var(--ink-soft)' } as const;

/** Quan es va llegir per última vegada, o que encara no s'ha llegit mai. */
function quan(last: string | null | undefined): string {
  return last === null || last === undefined
    ? t('settings.mail.neverPolled')
    : t('settings.mail.lastPolled', { when: dateTime(getLocale(), new Date(last)) });
}

function Camp({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={camp}>
      {label}
      {children}
    </label>
  );
}

/** Un compte, amb el seu estat i el botó de provar. */
function MailAccountRow({ account, onDone }: { account: MailAccount; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [pollInterval, setPollInterval] = useState(String(account.poll_interval ?? ''));
  const [result, setResult] = useState<MailTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const test = useMutation(async () => {
    setError(null);
    setResult(null);
    try {
      // **No desa res**, ni tan sols la contrasenya que s'hi escriu per provar.
      setResult(
        await api.post<MailTestResult>(`/api/v1/mail/accounts/${account.id}/test`, {
          password: password === '' ? undefined : password,
        }),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.generic'));
    }
  });

  const save = useMutation(async () => {
    setError(null);
    try {
      await api.patch<MailAccount>(`/api/v1/mail/accounts/${account.id}`, {
        // Buida vol dir «no la toquis».
        password: password === '' ? undefined : password,
        enabled: account.enabled,
        poll_interval: pollInterval === '' ? null : Number(pollInterval),
      });
      setPassword('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.generic'));
    }
  });

  const remove = useMutation(async () => {
    await api.delete<void>(`/api/v1/mail/accounts/${account.id}`);
    onDone();
  });

  return (
    <div
      data-testid={`mail-account-${account.id}`}
      style={{
        display: 'grid',
        gap: 8,
        padding: 12,
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{account.name}</strong>
        {/*
          **Un punt volat i no una arrova.** L'usuari d'IMAP sol ser una adreça sencera, i
          `jo@example.com@imap.example.com:993` es llegeix com una adreça mal escrita en
          comptes de com «aquest usuari, en aquest servidor».
        */}
        <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
          {account.username} · {account.host}:{account.port}
        </span>
      </div>

      {/*
        L'estat de l'últim refresc, amb el motiu si va fallar. Va a la fila i no només al
        registre pel mateix motiu que als calendaris: una font caiguda es veu exactament
        igual que una que no té res.
      */}
      <span
        style={{
          fontSize: 11.5,
          color:
            account.last_error === null || account.last_error === undefined
              ? 'var(--ink-soft)'
              : 'var(--danger-text)',
        }}
      >
        {account.last_error ?? quan(account.last_polled_at)}
      </span>

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Camp label={t('settings.mail.pollInterval')}>
          <select
            className="plou-input"
            value={pollInterval}
            onChange={(event) => setPollInterval(event.target.value)}
          >
            <option value="">{t('settings.mail.pollDefault')}</option>
            {[
              60,
              120,
              300,
              600,
              900,
              1800,
              3600,
              ...(![60, 120, 300, 600, 900, 1800, 3600].includes(Number(pollInterval)) &&
              pollInterval !== ''
                ? [Number(pollInterval)]
                : []),
            ].map((seconds) => (
              <option key={seconds} value={seconds}>
                {t('settings.mail.pollMinutes', { count: seconds / 60 })}
              </option>
            ))}
          </select>
        </Camp>
        {account.auth_method === 'google' ? (
          <span>{t(`settings.mail.oauth.${account.oauth_status ?? 'disconnected'}`)}</span>
        ) : (
          <Camp label={t('settings.mail.password')}>
            <input
              type="password"
              className="plou-input"
              data-testid={`mail-password-${account.id}`}
              value={password}
              /*
              Curt perquè **hi càpiga**: el text llarg es tallava a «Deixa-ho buit per r»,
              i un camp que et parla a mitges és el que et fa pensar que això va malament
              abans de fer-lo servir. La frase sencera va al `title`.
            */
              placeholder={account.has_secret ? t('settings.mail.passwordKept') : ''}
              title={account.has_secret ? t('settings.mail.passwordKept.long') : undefined}
              style={{ minWidth: 190 }}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Camp>
        )}
        <button type="button" className="plou-btn" onClick={() => void test.run()}>
          {test.busy ? t('settings.mail.testing') : t('settings.mail.test')}
        </button>
        <button
          type="button"
          className="plou-btn"
          disabled={save.busy}
          onClick={() => void save.run()}
        >
          {t('settings.mail.save')}
        </button>
        <button
          type="button"
          className="plou-btn plou-btn-ghost"
          data-testid={`mail-remove-${account.id}`}
          onClick={() => void remove.run()}
        >
          {t('settings.mail.remove')}
        </button>
      </div>

      {result !== null ? (
        <span
          data-testid={`mail-test-${account.id}`}
          style={{ fontSize: 11.5, color: result.ok ? 'var(--ink-soft)' : 'var(--danger-text)' }}
        >
          {result.ok
            ? t('settings.mail.testOk', { count: String(result.folders.length) })
            : t('settings.mail.testFail', { error: result.error ?? '' })}
        </span>
      ) : null}
      <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        {t('settings.mail.pollHelp')}
      </span>

      {/*
        **La pista que hauria estalviat una tarda.** «L'usuari o la contrasenya no són
        correctes» és cert i no serveix de res quan la contrasenya que llegeixes a la
        pantalla és la bona: el que falla és que el proveïdor no accepta la del compte, o
        que el que vas enganxar portava un espai que el camp no dibuixa.
      */}
      {result !== null && !result.ok && account.auth_method !== 'google' ? (
        <span
          data-testid={`mail-hint-${account.id}`}
          style={{ fontSize: 11, color: 'var(--ink-soft)' }}
        >
          {t('settings.mail.appPassword')}
        </span>
      ) : null}

      {error !== null ? (
        <p role="alert" style={{ fontSize: 11.5, color: 'var(--danger-text)', margin: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function MailAccountForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [security, setSecurity] = useState<'tls' | 'starttls'>('tls');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation(async () => {
    setError(null);
    try {
      await api.post<MailAccount>('/api/v1/mail/accounts', {
        id: uuidv7(),
        name: name.trim() === '' ? host.trim() : name.trim(),
        host: host.trim(),
        username: username.trim(),
        security,
        password: password === '' ? undefined : password,
      });
      setName('');
      setHost('');
      setUsername('');
      setPassword('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.generic'));
    }
  });

  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid="mail-account-form">
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Camp label={t('settings.mail.name')}>
          <input
            className="plou-input"
            data-testid="mail-new-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Camp>
        <Camp label={t('settings.mail.host')}>
          <input
            className="plou-input"
            data-testid="mail-new-host"
            value={host}
            onChange={(event) => setHost(event.target.value)}
          />
        </Camp>
        <Camp label={t('settings.mail.security')}>
          {/*
            **No hi ha «cap».** Oferir IMAP en clar vol dir que algú ho triarà i les seves
            credencials viatjaran nues per la xarxa de casa. El port el tria el servidor
            segons això: 993 amb TLS, 143 amb STARTTLS.
          */}
          <select
            className="plou-input"
            data-testid="mail-new-security"
            value={security}
            onChange={(event) =>
              setSecurity(event.target.value === 'starttls' ? 'starttls' : 'tls')
            }
          >
            <option value="tls">{t('settings.mail.security.tls')}</option>
            <option value="starttls">{t('settings.mail.security.starttls')}</option>
          </select>
        </Camp>
        <Camp label={t('settings.mail.username')}>
          <input
            className="plou-input"
            data-testid="mail-new-username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </Camp>
        <Camp label={t('settings.mail.password')}>
          <input
            type="password"
            className="plou-input"
            data-testid="mail-new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Camp>
        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="mail-add-account"
          onClick={() => void add.run()}
        >
          {t('settings.mail.add')}
        </button>
      </div>
      {error !== null ? (
        <p role="alert" style={{ fontSize: 11.5, color: 'var(--danger-text)', margin: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Els valors amb què es previsualitza una plantilla. Un correu creïble i prou. */
const MOSTRA = {
  subject: 'La factura de març',
  from_name: 'Escola',
  from_email: 'secretaria@escola.test',
  from: 'Escola',
  date: '11/08/2026',
  folder: 'INBOX/Escola',
  account: 'Personal',
};

function MailRuleRow({ rule, onDone }: { rule: MailRule; onDone: () => void }) {
  const { scopes } = useSessionData();
  const scope = scopes.find((s) => s.id === rule.scope_id);

  const remove = useMutation(async () => {
    await api.delete<void>(`/api/v1/mail/rules/${rule.id}`);
    onDone();
  });

  const desa = useMutation(async (next: boolean) => {
    await api.patch<MailRule>(`/api/v1/mail/rules/${rule.id}`, {
      inbox_visible: next === rule.inbox_visible_default ? null : next,
    });
    onDone();
  });

  return (
    <div
      data-testid={`mail-rule-${rule.id}`}
      style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}
    >
      <strong style={{ fontSize: 12.5 }}>{rule.folder}</strong>
      <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        → {scope?.name ?? rule.scope_id}
      </span>
      <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        {renderMailTitle(rule.title_template, MOSTRA, MOSTRA.subject)}
      </span>

      {/*
        **L'interruptor que abans no sortia enlloc.**

        Mateix component i mateixa semàntica que les fonts de calendari: es desa
        **l'excepció i no l'estat**, o sigui que si el valor coincideix amb el defecte
        s'envia `null`. Sense això, una carpeta es quedaria clavada al valor que se li va
        posar encara que el defecte canviés.
      */}
      <Toggle
        checked={rule.inbox_visible ?? rule.inbox_visible_default}
        onChange={(next) => void desa.run(next)}
        label={t('settings.mail.inboxVisible')}
        testId={`mail-rule-inbox-${rule.id}`}
      />
      <button
        type="button"
        className="plou-btn plou-btn-ghost"
        data-testid={`mail-rule-remove-${rule.id}`}
        onClick={() => void remove.run()}
      >
        {t('settings.mail.remove')}
      </button>
    </div>
  );
}

function MailRuleForm({ accounts, onDone }: { accounts: MailAccount[]; onDone: () => void }) {
  const { scopes } = useSessionData();
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const [folder, setFolder] = useState('');
  const [scopeId, setScopeId] = useState(scopes[0]?.id ?? '');
  const [template, setTemplate] = useState(DEFAULT_MAIL_TEMPLATE);
  const [error, setError] = useState<string | null>(null);

  /**
   * Les carpetes del servidor, per **triar-les**.
   *
   * Es demanen en triar el compte i no en obrir la pestanya: és una connexió IMAP, i
   * fer-la per si de cas voldria dir piconar el servidor de correu cada vegada que algú
   * mira els seus ajustos.
   */
  const folders = useApi<{ folders: string[]; delimiter: string | null; error: string | null }>(
    accountId === '' ? null : `/api/v1/mail/accounts/${accountId}/folders`,
  );
  const llista = folders.data?.folders ?? [];

  const desconegudes = unknownMailVars(template);

  const add = useMutation(async () => {
    setError(null);
    try {
      await api.post<MailRule>('/api/v1/mail/rules', {
        id: uuidv7(),
        account_id: accountId,
        folder: folder.trim(),
        scope_id: scopeId,
        title_template: template,
      });
      setFolder('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.generic'));
    }
  });

  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid="mail-rule-form">
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Camp label={t('settings.mail.accounts')}>
          <select
            className="plou-input"
            data-testid="mail-rule-account"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </Camp>
        <Camp label={t('settings.mail.folder')}>
          {/*
            **Es tria de la llista que diu el servidor**, i es pot escriure a mà.

            Era només text lliure, i el resultat era que la prova de connexió trobava onze
            carpetes, no en deia ni una, i havies d'endevinar-ne el nom exacte —amb els
            `[Gmail]/` i els accents que porti cada proveïdor. Una llista tancada tampoc:
            deixaria clavat qui tingui una carpeta que el servidor no anuncia.

            La llista **arriba sola** en triar el compte; si el servidor no contesta, queda
            el camp de sempre i no s'ha perdut res.
          */}
          {llista.length === 0 ? (
            <input
              className="plou-input"
              data-testid="mail-rule-folder"
              value={folder}
              onChange={(event) => setFolder(event.target.value)}
              /*
                Si el servidor no ha contestat, **es diu**. El `error` ja venia a la
                resposta i no es pintava enlloc: quedava un camp buit i mut, que sembla
                que no hagi passat res quan el que passa és que no s'han pogut llegir.
              */
              placeholder={
                folders.loading
                  ? t('settings.mail.loadingFolders')
                  : folders.data?.error != null
                    ? t('settings.mail.foldersFailed')
                    : t('settings.mail.folderPlaceholder')
              }
            />
          ) : (
            <select
              className="plou-input"
              data-testid="mail-rule-folder"
              value={folder}
              onChange={(event) => setFolder(event.target.value)}
            >
              <option value="">{t('settings.mail.pickFolder')}</option>
              {llista.map((path) => (
                <option key={path} value={path}>
                  {path}
                </option>
              ))}
            </select>
          )}
        </Camp>
        <Camp label={t('settings.mail.scope')}>
          <select
            className="plou-input"
            data-testid="mail-rule-scope"
            value={scopeId}
            onChange={(event) => setScopeId(event.target.value)}
          >
            {scopes.map((scope) => (
              <option key={scope.id} value={scope.id}>
                {scope.name}
              </option>
            ))}
          </select>
        </Camp>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Camp label={t('settings.mail.template')}>
          <input
            className="plou-input"
            data-testid="mail-rule-template"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
          />
        </Camp>
        <button
          type="button"
          className="plou-btn plou-btn-ghost"
          data-testid="mail-rule-preset"
          onClick={() => setTemplate('{{from_name}} - {{subject}}')}
        >
          {t('settings.mail.templatePreset')}
        </button>
        <button
          type="button"
          className="plou-btn plou-btn-primary"
          data-testid="mail-add-rule"
          onClick={() => void add.run()}
        >
          {t('settings.mail.addRule')}
        </button>
      </div>

      {/*
        La previsualització la fa la MATEIXA funció que el servidor. I les variables mal
        escrites s'avisen sense rebutjar-les: qui vulgui unes claus literals al títol està
        en el seu dret, i el que no ha de passar és que una errata sembli un camp buit.
      */}
      <span data-testid="mail-rule-preview" style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        {t('settings.mail.templatePreview', {
          preview: renderMailTitle(template, MOSTRA, MOSTRA.subject),
        })}
      </span>
      {desconegudes.length > 0 ? (
        <span style={{ fontSize: 11.5, color: 'var(--warning-text)' }}>
          {t('settings.mail.templateUnknown', { vars: desconegudes.join(', ') })}
        </span>
      ) : null}

      {error !== null ? (
        <p role="alert" style={{ fontSize: 11.5, color: 'var(--danger-text)', margin: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
